// ─────────────────────────────────────────────
//  linkStaffToAccounts — admin batch that links every staff record to its
//  Firebase Auth account so messaging channels + alert delivery can resolve a
//  roster member through staff.uid (A1).
//
//  WHY name-matching (not email): seeded `staff` docs carry NO email field
//  (see seedStaff.js — name/phone/role/shortCode/uid only), while `users` docs
//  carry name + firstName + lastName + a derived email. Matching on staff.email
//  therefore links NOBODY (every staff row is "no match"). Both collections
//  build the display name the same way (`${firstName} ${lastName}`), so a
//  normalized-name match is exact and immune to email-domain drift — the same
//  drift that forced the email-match self-link to be reverted (d676635).
//
//  Match order per staff: (1) staff.email → users.email when an email exists,
//  (2) normalized full name → users.name. Names that map to more than one user
//  are ambiguous and skipped (counted as no-match) so we never link the wrong
//  account. Admin-only (the staff update rule lets an admin set any field).
//  Idempotent: a staff row already pointing at the right uid is left alone.
// ─────────────────────────────────────────────
import { collection, getDocs, doc, updateDoc } from "./sdk";
import { db } from "./config";

// Letters only: lowercases and drops parentheticals, spaces, hyphens, dots,
// apostrophes — same shape used by domain/email.js so the two never drift.
export const normalizeName = (s) =>
  (s || "")
    .toLowerCase()
    .replace(/\(.*?\)/g, "")
    .replace(/[^a-z]/g, "");

// Pure planner: given the raw users + staff docs ({ id, ...data }), decide which
// staff rows need a uid stamp. Returns { toLink:[{id,uid}], alreadyLinked,
// unmatched, total }. No I/O — unit-tested in linkStaff.test.js.
export function planStaffLinks(users, staff) {
  // email (lowercased) → uid. First writer wins; collisions are rare for email.
  const emailToUid = new Map();
  // normalized name → uid, plus the set of names seen on >1 user (ambiguous).
  const nameToUid = new Map();
  const ambiguousNames = new Set();

  users.forEach((u) => {
    const uid = u.uid || u.id; // re-keyed accounts have doc.id === auth uid
    if (!uid) return;
    const email = (u.email || "").trim().toLowerCase();
    if (email && !emailToUid.has(email)) emailToUid.set(email, uid);

    const name = normalizeName(u.name);
    if (!name) return;
    if (nameToUid.has(name) && nameToUid.get(name) !== uid) {
      ambiguousNames.add(name);
    } else {
      nameToUid.set(name, uid);
    }
  });

  const toLink = [];
  let alreadyLinked = 0;
  let unmatched = 0;

  staff.forEach((s) => {
    const email = (s.email || "").trim().toLowerCase();
    const name = normalizeName(s.name);

    let targetUid = email ? emailToUid.get(email) : undefined;
    if (!targetUid && name && !ambiguousNames.has(name)) {
      targetUid = nameToUid.get(name);
    }

    if (!targetUid) {
      unmatched += 1;
    } else if (s.uid === targetUid) {
      alreadyLinked += 1;
    } else {
      toLink.push({ id: s.id, uid: targetUid });
    }
  });

  return { toLink, alreadyLinked, unmatched, total: staff.length };
}

// Returns { linked, alreadyLinked, unmatched, total }.
export async function linkStaffToAccounts(onProgress) {
  const [usersSnap, staffSnap] = await Promise.all([
    getDocs(collection(db, "users")),
    getDocs(collection(db, "staff")),
  ]);

  const users = usersSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
  const staff = staffSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

  const plan = planStaffLinks(users, staff);

  let linked = 0;
  let unmatched = plan.unmatched;
  for (let i = 0; i < plan.toLink.length; i += 1) {
    const { id, uid } = plan.toLink[i];
    try {
      await updateDoc(doc(db, "staff", id), { uid });
      linked += 1;
    } catch (e) {
      // A staff row already linked to a different uid is rejected by the rule
      // for non-admins; admins should not hit this. Count as unmatched.
      unmatched += 1;
    }
    if (onProgress) onProgress({ done: i + 1, total: plan.toLink.length });
  }

  return { linked, alreadyLinked: plan.alreadyLinked, unmatched, total: plan.total };
}
